// Notification Template Service for TradeMate Pro
// Handles email and SMS templates with feature flags

import { getCurrentBrand } from '../utils/brandingUtils';
import settingsService from './SettingsService';
import smsNumberService from './SmsNumberService';
import { fmtDate } from '../utils/dateFormatters';
import { paymentMethodLabel } from '../constants/paymentMethods';

class NotificationTemplateService {
  constructor() {
    this.templateCache = new Map();
  }

  // Check if email notifications are enabled
  // CHANNEL only. `send_quote_notifications` used to be ANDed in here, which made a flag
  // named for quotes silently govern every email this service sends — in practice the job
  // scheduled and job completed mails, since quote sending does not come through here at
  // all. Event flags are now applied per event in NotificationIntegrationService, where the
  // event type is actually known. Channel and event are different axes (PRODUCT_DECISIONS,
  // 2026-08-31); collapsing them into one boolean is what produced the mismatch.
  async isEmailEnabled(companyId) {
    try {
      const settings = await settingsService.getSettings(companyId);
      return settings?.email_notifications_enabled !== false;
    } catch (error) {
      console.warn('Failed to check email settings:', error);
      return false;
    }
  }

  // Check if SMS can go out: live number AND preference on (#4784 — SmsNumberService owns this)
  async isSmsEnabled(companyId) {
    return smsNumberService.canSendSms(companyId);
  }

  // Get company branding info for templates
  async getCompanyBranding(companyId) {
    try {
      const settings = await settingsService.getSettings(companyId);
      return {
        name: settings?.company_name || getCurrentBrand().name,
        logo: settings?.company_logo || null,
        phone: settings?.phone || null,
        email: settings?.email || null,
        website: settings?.website || null,
        address: settings?.address || null
      };
    } catch (error) {
      console.warn('Failed to get company branding:', error);
      return { name: getCurrentBrand().name };
    }
  }

  // Email Templates
  async generateEmailTemplate(companyId, templateType, data) {
    if (!(await this.isEmailEnabled(companyId))) {
      return null;
    }

    const branding = await this.getCompanyBranding(companyId);
    
    switch (templateType) {
      case 'QUOTE_SENT':
        return this.generateQuoteSentEmail(branding, data);
      case 'QUOTE_APPROVED':
        return this.generateQuoteApprovedEmail(branding, data);
      case 'QUOTE_REJECTED':
        return this.generateQuoteRejectedEmail(branding, data);
      case 'QUOTE_CHANGES_REQUESTED':
        return this.generateQuoteChangesRequestedEmail(branding, data);
      case 'APPOINTMENT_REMINDER':
        return this.generateAppointmentReminderEmail(branding, data);
      case 'INVOICE_OVERDUE':
        return this.generateInvoiceOverdueEmail(branding, data);
      case 'PAYMENT_RECEIVED':
        return this.generatePaymentReceivedEmail(branding, data);
      case 'WORK_COMPLETED':
        return this.generateWorkCompletedEmail(branding, data);
      default:
        return null;
    }
  }

  // SMS Templates
  async generateSmsTemplate(companyId, templateType, data) {
    if (!(await this.isSmsEnabled(companyId))) {
      return null;
    }

    const branding = await this.getCompanyBranding(companyId);
    
    switch (templateType) {
      case 'APPOINTMENT_REMINDER':
        return this.generateAppointmentReminderSms(branding, data);
      case 'QUOTE_APPROVED':
        return this.generateQuoteApprovedSms(branding, data);
      case 'URGENT_UPDATE':
        return this.generateUrgentUpdateSms(branding, data);
      default:
        return null;
    }
  }

  // Email Template Generators
  generateQuoteSentEmail(branding, { quote, customer }) {
    return {
      subject: `Quote #${quote.quote_number} from ${branding.name}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; text-align: center;">
            <h1>${branding.name}</h1>
            <h2>Your Quote is Ready!</h2>
          </div>
          <div style="padding: 20px; background: #f9f9f9;">
            <p>Dear ${customer.name || 'Valued Customer'},</p>
            <p>We've prepared a quote for your project. Please review the details below:</p>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3>Quote #${quote.quote_number}</h3>
              <p><strong>Total Amount:</strong> $${quote.total_amount}</p>
              <p><strong>Valid Until:</strong> ${quote.expires_date}</p>
            </div>
            <p>If you have any questions, please don't hesitate to contact us.</p>
            <p>Best regards,<br>${branding.name}</p>
            ${branding.phone ? `<p>Phone: ${branding.phone}</p>` : ''}
            ${branding.email ? `<p>Email: ${branding.email}</p>` : ''}
          </div>
        </div>
      `,
      text: `Quote #${quote.quote_number} from ${branding.name}\n\nDear ${customer.name},\n\nYour quote is ready for $${quote.total_amount}. Valid until ${quote.expires_date}.\n\nContact us with any questions.\n\nBest regards,\n${branding.name}`
    };
  }

  generateQuoteApprovedEmail(branding, { quote, customer }) {
    return {
      subject: `Quote Approved - Thank You! - ${branding.name}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%); color: white; padding: 20px; text-align: center;">
            <h1>${branding.name}</h1>
            <h2>🎉 Quote Approved!</h2>
          </div>
          <div style="padding: 20px; background: #f9f9f9;">
            <p>Dear ${customer.name || 'Valued Customer'},</p>
            <p>Thank you for approving quote #${quote.quote_number}! We're excited to work with you.</p>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3>Next Steps:</h3>
              <ul>
                <li>We'll schedule your project within 2 business days</li>
                <li>You'll receive a confirmation with appointment details</li>
                <li>Our team will contact you to coordinate timing</li>
              </ul>
            </div>
            <p>We appreciate your business and look forward to exceeding your expectations!</p>
            <p>Best regards,<br>${branding.name}</p>
          </div>
        </div>
      `,
      text: `Quote Approved - Thank You!\n\nDear ${customer.name},\n\nThank you for approving quote #${quote.quote_number}! We'll schedule your project within 2 business days.\n\nBest regards,\n${branding.name}`
    };
  }

  generateQuoteRejectedEmail(branding, { quote, customer }) {
    return {
      subject: `Quote Declined - ${branding.name}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #9e9e9e 0%, #757575 100%); color: white; padding: 20px; text-align: center;">
            <h1>${branding.name}</h1>
            <h2>Quote Declined</h2>
          </div>
          <div style="padding: 20px; background: #f9f9f9;">
            <p>Hi ${branding.name},</p>
            <p>Customer ${customer.name || ''} has declined quote #${quote.quote_number}.</p>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3>Quote Details:</h3>
              <p><strong>Quote #:</strong> ${quote.quote_number}</p>
              <p><strong>Amount:</strong> $${quote.total_amount || '0.00'}</p>
              <p><strong>Address:</strong> ${quote.job_address || 'N/A'}</p>
            </div>
            <p>You may want to follow up with the customer to understand their decision.</p>
            <p>Best regards,<br>${branding.name}</p>
          </div>
        </div>
      `,
      text: `Quote Declined\n\nCustomer ${customer.name || ''} has declined quote #${quote.quote_number}.\n\nAmount: $${quote.total_amount || '0.00'}\nAddress: ${quote.job_address || 'N/A'}\n\nBest regards,\n${branding.name}`
    };
  }

  generateQuoteChangesRequestedEmail(branding, { quote, customer, message }) {
    return {
      subject: `Changes Requested - Quote #${quote.quote_number} - ${branding.name}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%); color: white; padding: 20px; text-align: center;">
            <h1>${branding.name}</h1>
            <h2>Changes Requested</h2>
          </div>
          <div style="padding: 20px; background: #f9f9f9;">
            <p>Hi ${branding.name},</p>
            <p>Customer ${customer.name || ''} has requested changes on quote #${quote.quote_number}.</p>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3>Customer Message:</h3>
              <p style="font-style: italic; color: #666;">${message || 'No message provided'}</p>
            </div>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3>Quote Details:</h3>
              <p><strong>Quote #:</strong> ${quote.quote_number}</p>
              <p><strong>Amount:</strong> $${quote.total_amount || '0.00'}</p>
              <p><strong>Address:</strong> ${quote.job_address || 'N/A'}</p>
            </div>
            <p>Please review the customer's request and follow up as needed.</p>
            <p>Best regards,<br>${branding.name}</p>
          </div>
        </div>
      `,
      text: `Changes Requested on Quote #${quote.quote_number}\n\nCustomer ${customer.name || ''} has requested changes.\n\nMessage: ${message || 'No message'}\n\nAmount: $${quote.total_amount || '0.00'}\nAddress: ${quote.job_address || 'N/A'}\n\nBest regards,\n${branding.name}`
    };
  }

  generateAppointmentReminderEmail(branding, { appointment, customer }) {
    return {
      subject: `Appointment Reminder - ${branding.name}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%); color: white; padding: 20px; text-align: center;">
            <h1>${branding.name}</h1>
            <h2>⏰ Appointment Reminder</h2>
          </div>
          <div style="padding: 20px; background: #f9f9f9;">
            <p>Dear ${customer.name || 'Valued Customer'},</p>
            <p>This is a friendly reminder about your upcoming appointment:</p>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3>Appointment Details</h3>
              <p><strong>Date:</strong> ${appointment.scheduled_date}</p>
              <p><strong>Time:</strong> ${appointment.scheduled_time}</p>
              <p><strong>Service:</strong> ${appointment.service_description}</p>
              ${appointment.address ? `<p><strong>Location:</strong> ${appointment.address}</p>` : ''}
            </div>
            <p>Please ensure someone is available at the scheduled time. If you need to reschedule, please contact us as soon as possible.</p>
            <p>Best regards,<br>${branding.name}</p>
            ${branding.phone ? `<p>Phone: ${branding.phone}</p>` : ''}
          </div>
        </div>
      `,
      text: `Appointment Reminder from ${branding.name}\n\nDear ${customer.name},\n\nReminder: ${appointment.scheduled_date} at ${appointment.scheduled_time}\nService: ${appointment.service_description}\n\nContact us to reschedule if needed.\n\nBest regards,\n${branding.name}`
    };
  }

  generateInvoiceOverdueEmail(branding, { invoice, customer }) {
    return {
      subject: `Payment Reminder - Invoice #${invoice.invoice_number}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%); color: white; padding: 20px; text-align: center;">
            <h1>${branding.name}</h1>
            <h2>Payment Reminder</h2>
          </div>
          <div style="padding: 20px; background: #f9f9f9;">
            <p>Dear ${customer.name || 'Valued Customer'},</p>
            <p>We hope you're satisfied with our recent work. This is a friendly reminder that payment for the following invoice is now overdue:</p>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #f44336;">
              <h3>Invoice #${invoice.invoice_number}</h3>
              <p><strong>Amount Due:</strong> $${invoice.total_amount}</p>
              <p><strong>Due Date:</strong> ${invoice.due_date}</p>
              <p><strong>Days Overdue:</strong> ${Math.ceil((new Date() - new Date(invoice.due_date)) / (1000 * 60 * 60 * 24))}</p>
            </div>
            <p>Please remit payment at your earliest convenience. If you have any questions or concerns, please contact us immediately.</p>
            <p>Thank you for your prompt attention to this matter.</p>
            <p>Best regards,<br>${branding.name}</p>
          </div>
        </div>
      `,
      text: `Payment Reminder - Invoice #${invoice.invoice_number}\n\nDear ${customer.name},\n\nYour payment of $${invoice.total_amount} is now overdue (due ${invoice.due_date}).\n\nPlease remit payment or contact us with any questions.\n\nBest regards,\n${branding.name}`
    };
  }

  generatePaymentReceivedEmail(branding, { payment, invoice, customer }) {
    return {
      subject: `Payment Received - Thank You! - ${branding.name}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%); color: white; padding: 20px; text-align: center;">
            <h1>${branding.name}</h1>
            <h2>✅ Payment Received</h2>
          </div>
          <div style="padding: 20px; background: #f9f9f9;">
            <p>Dear ${customer.name || 'Valued Customer'},</p>
            <p>Thank you! We've received your payment for invoice #${invoice.invoice_number}.</p>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3>Payment Details</h3>
              <p><strong>Amount:</strong> $${payment.amount}</p>
              <p><strong>Payment Method:</strong> ${paymentMethodLabel(payment) || payment.payment_method}</p>
              <p><strong>Date:</strong> ${fmtDate(new Date())}</p>
              <p><strong>Transaction ID:</strong> ${payment.transaction_id || 'N/A'}</p>
            </div>
            <p>Your account is now current. We appreciate your business and look forward to serving you again!</p>
            <p>Best regards,<br>${branding.name}</p>
          </div>
        </div>
      `,
      text: `Payment Received - Thank You!\n\nDear ${customer.name},\n\nWe've received your payment of $${payment.amount} for invoice #${invoice.invoice_number}.\n\nThank you for your business!\n\nBest regards,\n${branding.name}`
    };
  }

  generateWorkCompletedEmail(branding, { workOrder, customer }) {
    return {
      subject: `Work Completed - ${branding.name}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%); color: white; padding: 20px; text-align: center;">
            <h1>${branding.name}</h1>
            <h2>🔧 Work Completed</h2>
          </div>
          <div style="padding: 20px; background: #f9f9f9;">
            <p>Dear ${customer.name || 'Valued Customer'},</p>
            <p>We're pleased to inform you that your work order has been completed!</p>
            <div style="background: white; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <h3>Work Order #${workOrder.id}</h3>
              <p><strong>Service:</strong> ${workOrder.title || workOrder.description}</p>
              <p><strong>Completed:</strong> ${fmtDate(new Date())}</p>
            </div>
            <p>We hope you're satisfied with our work. If you have any questions or concerns, please don't hesitate to contact us.</p>
            <p>Thank you for choosing ${branding.name}!</p>
            <p>Best regards,<br>${branding.name}</p>
          </div>
        </div>
      `,
      text: `Work Completed - ${branding.name}\n\nDear ${customer.name},\n\nYour work order #${workOrder.id} has been completed.\n\nThank you for choosing ${branding.name}!\n\nBest regards,\n${branding.name}`
    };
  }

  // SMS Template Generators
  generateAppointmentReminderSms(branding, { appointment, customer }) {
    return {
      message: `${branding.name}: Reminder - Your appointment is ${appointment.scheduled_date} at ${appointment.scheduled_time}. ${appointment.service_description}. Call ${branding.phone || 'us'} to reschedule if needed.`
    };
  }

  generateQuoteApprovedSms(branding, { quote, customer }) {
    return {
      message: `${branding.name}: Thank you for approving quote #${quote.quote_number}! We'll schedule your project within 2 business days. Questions? Call ${branding.phone || 'us'}.`
    };
  }

  generateUrgentUpdateSms(branding, { message, customer }) {
    return {
      message: `${branding.name} URGENT: ${message}. Please call ${branding.phone || 'us'} immediately.`
    };
  }

  // Email dispatch is NOT implemented here.
  //
  // This used to console.log and return `{ success: true, messageId: 'email_<ts>' }`
  // for a message it never sent — a fabricated id and a false success that no
  // caller could detect, since none of them throw on a truthy result.
  //
  // It is currently unreachable: _processEmailNotification only maps QUOTE_SENT,
  // QUOTE_APPROVED, APPOINTMENT_REMINDER, INVOICE_OVERDUE, PAYMENT_RECEIVED and
  // WORK_ORDER_COMPLETED, and no live caller sends any of those. Reporting failure
  // therefore cannot regress anything today, and it removes the trap for whoever
  // adds one of those events later.
  //
  // Real customer email goes through MessageEmailService (brand-aware) or the
  // message_email_queue + process-message-emails pair. Wiring this to either is a
  // routing decision, not a mechanical fix. See docs/NOTIFICATION_FAKE_SUCCESS_AUDIT.md.
  async sendEmail(companyId, templateType, data, recipient) {
    const template = await this.generateEmailTemplate(companyId, templateType, data);
    if (!template) return null;

    console.warn('NotificationTemplateService.sendEmail is not implemented; no email sent.', {
      to: recipient,
      template: templateType
    });

    return {
      success: false,
      notImplemented: true,
      error: 'Email dispatch is not implemented for this notification type',
      template: templateType
    };
  }

  // SMS dispatch is NOT implemented here — same false-success problem as above.
  //
  // Do NOT wire this to twilioService without an explicit routing decision. SMS is
  // a prepaid add-on: 62 of 63 companies have sms_notifications_enabled = true, so
  // pointing this at Twilio would start consuming purchased credits automatically
  // on events nobody opted into. The real, metered path is
  // TwilioService -> send-sms -> sms_messages.
  async sendSms(companyId, templateType, data, recipient) {
    const template = await this.generateSmsTemplate(companyId, templateType, data);
    if (!template) return null;

    console.warn('NotificationTemplateService.sendSms is not implemented; no SMS sent.', {
      to: recipient,
      template: templateType
    });

    return {
      success: false,
      notImplemented: true,
      error: 'SMS dispatch is not implemented for this notification type',
      template: templateType
    };
  }
}

const notificationTemplateService = new NotificationTemplateService();
export default notificationTemplateService;
